import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { GetCostCenter } from '../../../../../../graphql/costCenter';
import { useAccounts } from '../../../../../../context/AccountsContext';
import { formatMoneyWithCurrency } from '../../../../../../utils/formatter';
import AddDashedOutlineButton from './AddDashedOutlineButton';
import JournalEntryCostCenterSelect from './JournalEntryCostCenterSelect';
import {
  createEmptyByAccountRow,
  createEmptyGeneralRow,
  defaultCapAmountString,
  formatPercentFromAmount,
  getAccountDisplayParts,
  getEntryLineAmount,
  getIncomeExpenseCapTotal,
  countDistinctAssignedCostCenters,
  hydrateAssignmentFromEntryLines,
  isIncomeExpenseAccount,
  parseCostCenterAmountInput,
  isAssignmentCapReached,
  sumRowAmounts,
} from './journalEntryCostCenters';
import './JournalEntryCostCentersPanel.scss';

const CONFIG_CC_URL = '/configuracion#centros-costo';

const JournalEntryCostCentersPanel = forwardRef(({
  journalEntries,
  companyId,
  panelOpen,
  onDiscardAssignment,
  hydrateEntryId = null,
  hydrateEntryLines = null,
  onAssignmentChange,
}, ref) => {
  const { accounts } = useAccounts();
  const [mode, setMode] = useState('general');
  const generalAssignmentCap = useMemo(
    () => getIncomeExpenseCapTotal(journalEntries),
    [journalEntries],
  );

  const [generalRows, setGeneralRows] = useState(() => [
    createEmptyGeneralRow(defaultCapAmountString(getIncomeExpenseCapTotal(journalEntries))),
  ]);
  const [byAccountMap, setByAccountMap] = useState({});
  const suppressGeneralAutofillRef = useRef(false);

  const { data: costCenterData, reexecuteQuery } = GetCostCenter(companyId);
  const catalogList = costCenterData?.getManyCostCenter || [];

  const catalogOptions = useMemo(
    () =>
      catalogList.map((cc) => ({
        id: cc.id,
        name: cc.name,
        icon: cc.icon,
        color: cc.color,
      })),
    [catalogList],
  );

  const incomeExpenseLines = useMemo(
    () => (journalEntries || []).filter((e) => isIncomeExpenseAccount(e.account)),
    [journalEntries],
  );

  const resetAssignmentState = () => {
    suppressGeneralAutofillRef.current = true;
    setMode('general');
    setGeneralRows([createEmptyGeneralRow()]);
    setByAccountMap({});
  };

  useImperativeHandle(ref, () => ({
    getAssignmentState: () => ({ mode, generalRows, byAccountMap }),
    resetAssignment: resetAssignmentState,
    loadAssignment: (assignment) => {
      if (!assignment?.hasAny) {
        resetAssignmentState();
        return;
      }
      setMode(assignment.mode || 'byAccount');
      setGeneralRows(
        assignment.generalRows?.length
          ? assignment.generalRows
          : [createEmptyGeneralRow()],
      );
      setByAccountMap(assignment.byAccountMap || {});
      suppressGeneralAutofillRef.current = true;
    },
    hasSelection: () =>
      mode === 'byAccount'
        ? Object.values(byAccountMap || {}).some((rows) =>
            (rows || []).some((r) => r.costCenterId),
          )
        : (generalRows || []).some((r) => r.costCenterId),
    getCatalogList: () => catalogList,
  }), [mode, generalRows, byAccountMap, catalogList]);

  useEffect(() => {
    if (!hydrateEntryId || !hydrateEntryLines?.length) return;
    const hydrated = hydrateAssignmentFromEntryLines(hydrateEntryLines, catalogList);
    if (!hydrated.hasAny) return;
    setMode(hydrated.mode || 'byAccount');
    setGeneralRows(
      hydrated.generalRows?.length
        ? hydrated.generalRows
        : [createEmptyGeneralRow()],
    );
    setByAccountMap(hydrated.byAccountMap || {});
    suppressGeneralAutofillRef.current = true;
  }, [hydrateEntryId, hydrateEntryLines, catalogList]);

  useEffect(() => {
    if (!onAssignmentChange) return;
    onAssignmentChange(
      countDistinctAssignedCostCenters({ mode, generalRows, byAccountMap }),
    );
  }, [mode, generalRows, byAccountMap, onAssignmentChange]);

  useEffect(() => {
    if (panelOpen && companyId) {
      reexecuteQuery({ requestPolicy: 'network-only' });
    }
  }, [panelOpen, companyId, reexecuteQuery]);

  useEffect(() => {
    const onFocus = () => {
      if (panelOpen && companyId) {
        reexecuteQuery({ requestPolicy: 'network-only' });
      }
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [panelOpen, companyId, reexecuteQuery]);

  useEffect(() => {
    if (mode !== 'general') return;
    if (suppressGeneralAutofillRef.current || generalAssignmentCap < 0.01) return;
    setGeneralRows((rows) => {
      if (rows.length !== 1) return rows;
      const only = rows[0];
      if (only.costCenterId || parseCostCenterAmountInput(only.amount) >= 0.01) {
        return rows;
      }
      return [{ ...only, amount: defaultCapAmountString(generalAssignmentCap) }];
    });
  }, [mode, generalAssignmentCap]);

  useEffect(() => {
    if (mode !== 'byAccount') return;
    setByAccountMap((prev) => {
      const next = { ...prev };
      incomeExpenseLines.forEach((line) => {
        const rankingKey = String(line.ranking);
        const lineAmount = getEntryLineAmount(line);
        const existing = next[rankingKey] || next[line.ranking];
        if (existing?.some((r) => r.costCenterId)) {
          return;
        }
        if (!existing?.length) {
          next[rankingKey] = [
            createEmptyByAccountRow(defaultCapAmountString(lineAmount)),
          ];
          return;
        }
        if (
          existing.length === 1 &&
          !existing[0].costCenterId &&
          parseCostCenterAmountInput(existing[0].amount) < 0.01 &&
          lineAmount >= 0.01
        ) {
          next[rankingKey] = [
            { ...existing[0], amount: defaultCapAmountString(lineAmount) },
          ];
        }
      });
      return next;
    });
  }, [mode, incomeExpenseLines]);

  const defaultAmountForNewGeneralRow = () => {
    const sum = sumRowAmounts(generalRows);
    if (generalAssignmentCap > 0 && sum < generalAssignmentCap - 0.01) {
      return Math.round((generalAssignmentCap - sum) * 100) / 100;
    }
    return generalAssignmentCap > 0 ? generalAssignmentCap : '';
  };

  const updateGeneralRow = (index, patch) => {
    setGeneralRows((rows) =>
      rows.map((r, i) => (i === index ? { ...r, ...patch } : r)),
    );
  };

  const handleGeneralAmountChange = (index, value) => {
    const cap = generalAssignmentCap;
    let parsed = parseCostCenterAmountInput(value);
    if (cap > 0) {
      const others = generalRows.reduce((s, r, i) => {
        if (i === index) return s;
        return s + parseCostCenterAmountInput(r.amount);
      }, 0);
      const maxForRow = Math.max(0, cap - others);
      if (parsed > maxForRow) parsed = maxForRow;
    }
    updateGeneralRow(index, { amount: value === '' ? '' : String(parsed) });
  };

  const addGeneralRow = () => {
    setGeneralRows((rows) => [...rows, createEmptyGeneralRow(defaultAmountForNewGeneralRow())]);
  };

  const removeGeneralRow = (index) => {
    setGeneralRows((rows) => {
      if (rows.length <= 1) {
        return [createEmptyGeneralRow(defaultCapAmountString(generalAssignmentCap))];
      }
      return rows.filter((_, i) => i !== index);
    });
  };

  const getByAccountRows = (ranking) =>
    byAccountMap[String(ranking)] || byAccountMap[ranking] || [createEmptyByAccountRow()];

  const updateByAccountRow = (ranking, index, patch) => {
    const rankingKey = String(ranking);
    setByAccountMap((prev) => {
      const rows = [...(prev[rankingKey] || prev[ranking] || [createEmptyByAccountRow()])];
      rows[index] = { ...rows[index], ...patch };
      return { ...prev, [rankingKey]: rows };
    });
  };

  const handleByAccountAmountChange = (ranking, lineAmount, index, value) => {
    let parsed = parseCostCenterAmountInput(value);
    const rows = getByAccountRows(ranking);
    const others = rows.reduce((s, r, i) => {
      if (i === index) return s;
      return s + parseCostCenterAmountInput(r.amount);
    }, 0);
    const maxForRow = Math.max(0, lineAmount - others);
    if (parsed > maxForRow) parsed = maxForRow;
    updateByAccountRow(ranking, index, { amount: value === '' ? '' : String(parsed) });
  };

  const addByAccountRow = (ranking, lineAmount) => {
    setByAccountMap((prev) => {
      const rows = [...(prev[ranking] || [createEmptyByAccountRow()])];
      const sum = sumRowAmounts(rows);
      const defaultAmt =
        lineAmount > 0 && sum < lineAmount - 0.01
          ? Math.round((lineAmount - sum) * 100) / 100
          : lineAmount > 0
            ? lineAmount
            : '';
      rows.push(createEmptyByAccountRow(defaultAmt));
      return { ...prev, [ranking]: rows };
    });
  };

  const removeByAccountRow = (ranking, lineAmount, index) => {
    setByAccountMap((prev) => {
      const rows = [...(prev[ranking] || [createEmptyByAccountRow()])];
      if (rows.length <= 1) {
        return {
          ...prev,
          [ranking]: [createEmptyByAccountRow(defaultCapAmountString(lineAmount))],
        };
      }
      return { ...prev, [ranking]: rows.filter((_, i) => i !== index) };
    });
  };

  const generalAssignedSum = sumRowAmounts(generalRows);
  const generalCapReached = isAssignmentCapReached(
    generalAssignedSum,
    generalAssignmentCap,
  );

  const handleDiscardAssignment = () => {
    resetAssignmentState();
    onDiscardAssignment?.();
  };

  return (
    <div className="journal-entry-cc-panel">
      <div className="journal-entry-cc-panel__inner">
        <div className="journal-entry-cc-panel__mode-row">
          <div className="journal-entry-cc-panel__mode" role="radiogroup" aria-label="Modo de asignación">
            <button
              type="button"
              role="radio"
              aria-checked={mode === 'general'}
              className={`journal-entry-cc-mode-btn${mode === 'general' ? ' journal-entry-cc-mode-btn--active' : ''}`}
              onClick={() => setMode('general')}
            >
              General (sobre el asiento)
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={mode === 'byAccount'}
              className={`journal-entry-cc-mode-btn${mode === 'byAccount' ? ' journal-entry-cc-mode-btn--active' : ''}`}
              onClick={() => setMode('byAccount')}
            >
              Por cuenta contable
            </button>
          </div>
          <button
            type="button"
            className="journal-entry-cc-discard-btn"
            onClick={handleDiscardAssignment}
            title="Cerrar y no aplicar centros de costo al guardar"
            aria-label="No aplicar centros de costo"
          >
            <span className="journal-entry-cc-discard-btn__icon" aria-hidden>
              ×
            </span>
            <span className="journal-entry-cc-discard-btn__label">No aplicar Centros de Costo</span>
          </button>
        </div>

        <p className="journal-entry-cc-help-text">
          Si falta un centro de costo, créalo en configuración.{' '}
          <a
            href={CONFIG_CC_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="journal-entry-cc-help-link"
          >
            Gestionar centros de costo
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12" aria-hidden>
              <line x1="5" y1="19" x2="19" y2="5" />
              <polyline points="7 5 19 5 19 17" />
            </svg>
          </a>
        </p>

        {mode === 'general' ? (
          <>
            {generalRows.map((row, index) => {
              const amountNum = parseCostCenterAmountInput(row.amount);
              return (
                <div key={row.rowKey} className="journal-entry-cc-row">
                  <div className="journal-entry-cc-row__cc">
                    <span className="journal-entry-cc-field-label">Centro de costo</span>
                    <JournalEntryCostCenterSelect
                      options={catalogOptions}
                      value={row.costCenterId || ''}
                      displayLabel={row.costCenterLabel}
                      onChange={(id) => {
                        if (id) suppressGeneralAutofillRef.current = false;
                        updateGeneralRow(index, {
                          costCenterId: id,
                          costCenterLabel: undefined,
                          createdBy: 'manual',
                        });
                      }}
                      disabled={generalAssignmentCap < 0.01}
                    />
                  </div>
                  <div className="journal-entry-cc-row__pct">
                    <span className="journal-entry-cc-field-label">%</span>
                    <input
                      type="text"
                      className="journal-entry-cc-pct-input"
                      readOnly
                      value={formatPercentFromAmount(amountNum, generalAssignmentCap)}
                      tabIndex={-1}
                      aria-label="Porcentaje del total"
                    />
                  </div>
                  <div className="journal-entry-cc-row__amount">
                    <span className="journal-entry-cc-field-label">Monto</span>
                    <input
                      type="text"
                      className="journal-entry-cc-amount-input"
                      value={row.amount}
                      onChange={(e) => handleGeneralAmountChange(index, e.target.value)}
                      placeholder="0"
                      inputMode="decimal"
                      disabled={generalAssignmentCap < 0.01}
                    />
                  </div>
                  <button
                    type="button"
                    className="journal-entry-cc-row__remove"
                    onClick={() => removeGeneralRow(index)}
                    title="Quitar fila"
                    aria-label="Quitar fila"
                  >
                    ×
                  </button>
                </div>
              );
            })}
            <AddDashedOutlineButton
              onClick={addGeneralRow}
              disabled={generalAssignmentCap < 0.01 || generalCapReached}
            >
              Agregar centro de costo
            </AddDashedOutlineButton>
          </>
        ) : incomeExpenseLines.length === 0 ? (
          <p className="journal-entry-cc-empty">
            No hay líneas con cuentas de ingreso (3) o gasto (4). Agrega al menos una cuenta 3 o 4 al asiento.
          </p>
        ) : (
          incomeExpenseLines.map((line) => {
            const lineAmount = getEntryLineAmount(line);
            const rows = getByAccountRows(line.ranking);
            const { key: accountKey, name: accountName } = getAccountDisplayParts(
              line.account,
              accounts,
            );
            return (
              <div key={line.ranking} className="journal-entry-cc-account-block">
                <div className="journal-entry-cc-account-block__head">
                  Cuenta {accountKey}
                  {accountName ? (
                    <>
                      {' '}
                      <span className="journal-entry-cc-account-block__name">
                        {accountName}
                      </span>
                    </>
                  ) : null}
                  <span className="journal-entry-cc-account-block__meta">
                    {' '}
                    — Total a asignar: {formatMoneyWithCurrency(lineAmount, 'CLP')}
                  </span>
                </div>
                {rows.map((row, index) => {
                  const amountNum = parseCostCenterAmountInput(row.amount);
                  return (
                    <div key={row.rowKey} className="journal-entry-cc-row">
                      <div className="journal-entry-cc-row__cc">
                        <span className="journal-entry-cc-field-label">Centro de costo</span>
                        <JournalEntryCostCenterSelect
                          options={catalogOptions}
                          value={row.costCenterId || ''}
                          displayLabel={row.costCenterLabel}
                          onChange={(id) =>
                            updateByAccountRow(line.ranking, index, {
                              costCenterId: id,
                              costCenterLabel: undefined,
                              createdBy: 'manual',
                            })
                          }
                          disabled={lineAmount < 0.01}
                        />
                      </div>
                      <div className="journal-entry-cc-row__pct">
                        <span className="journal-entry-cc-field-label">%</span>
                        <input
                          type="text"
                          className="journal-entry-cc-pct-input"
                          readOnly
                          value={formatPercentFromAmount(amountNum, lineAmount)}
                          tabIndex={-1}
                        />
                      </div>
                      <div className="journal-entry-cc-row__amount">
                        <span className="journal-entry-cc-field-label">Monto</span>
                        <input
                          type="text"
                          className="journal-entry-cc-amount-input"
                          value={row.amount}
                          onChange={(e) =>
                            handleByAccountAmountChange(
                              line.ranking,
                              lineAmount,
                              index,
                              e.target.value,
                            )
                          }
                          placeholder="0"
                          inputMode="decimal"
                          disabled={lineAmount < 0.01}
                        />
                      </div>
                      <button
                        type="button"
                        className="journal-entry-cc-row__remove"
                        onClick={() => removeByAccountRow(line.ranking, lineAmount, index)}
                        aria-label="Quitar fila"
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
                <AddDashedOutlineButton
                  onClick={() => addByAccountRow(line.ranking, lineAmount)}
                  disabled={
                    lineAmount < 0.01 ||
                    isAssignmentCapReached(sumRowAmounts(rows), lineAmount)
                  }
                >
                  Agregar centro de costo
                </AddDashedOutlineButton>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
});

JournalEntryCostCentersPanel.displayName = 'JournalEntryCostCentersPanel';

export default JournalEntryCostCentersPanel;
